import { HttpRequest, HttpHandlerFn, HttpEvent, HttpResponse } from "@angular/common/http";
import { inject } from "@angular/core";
import { finalize, map, Observable, switchMap, tap } from "rxjs";
import { AuditService } from '../services/audit.service';
import {
  DISABLE_LOADER,
  DISABLE_ORG_SWITCHING,
  ANONYMOUS_REQUEST,
} from "../pages/auth/services/auth.service";
import { AuthStore } from "../pages/auth/store/auth.store";
import { environment } from "../../environments/environment";
import { GlobalStore } from "../store/global.store";
import { isPlatformServiceStaff } from '../pages/auth/model/role.model';

export function jwtInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  const authStore = inject(AuthStore);
  const globalStore = inject(GlobalStore);
  const audit = inject(AuditService);

  const token = authStore.accessToken();

  const isLoaderEnabled =
    !req.context.get(DISABLE_LOADER) &&
    req.method == "GET" &&
    req.url.startsWith(environment.baseUrl);

  const disableOrgSwitching = req.context.get(DISABLE_ORG_SWITCHING);

  if (isLoaderEnabled) {
    globalStore.startRequest(req.url);
  }

  if (!req.url.startsWith(environment.baseUrl)) return next(req.clone());
  if (req.context.get(ANONYMOUS_REQUEST)) {
    return next(req).pipe(
      finalize(() => {
        if (isLoaderEnabled) {
          globalStore.endRequest(req.url);
        }
      }),
    );
  }

  // Existing API header scoping revalidates the staff assignment on every
  // request. Keep the explicit selection usable when a browser blocks the
  // cross-site org cookie (e.g. HTTP SPA -> HTTPS API in private browsing).
  const orgId = authStore.activeOrganizationId?.();
  const path = req.url.split('?')[0].toLowerCase();
  const selectionCommand = ['/me/sessions/switch-org', '/me/sessions/clear-org', '/me/organizations', '/me/role']
    .some(suffix => path.endsWith(suffix));
  if (!disableOrgSwitching && !selectionCommand && isPlatformServiceStaff(authStore.role?.())
    && orgId && Number.isInteger(orgId) && orgId > 0) {
    req = req.clone({ setHeaders: { 'X-OrgId': String(orgId) } });
  }

  let request: Observable<HttpEvent<unknown>>;

  if (token) request = next(addToken(req, token, disableOrgSwitching));
  else
    request = authStore.authService.getToken().pipe(
      map((token) => token?.tokens?.accessToken),
      switchMap((accessToken) => {
        if (!accessToken) return next(req);
        authStore.setToken(accessToken.toString());
        return next(addToken(req, accessToken.toString(), disableOrgSwitching));
      }),
    );

  return request.pipe(
    tap(event => {
      if (event instanceof HttpResponse && event.ok && !['GET', 'HEAD', 'OPTIONS'].includes(req.method)
        && !req.url.split('?')[0].endsWith('/Audit/history-presence')) audit.invalidateHistory();
    }),
    finalize(() => {
      if (isLoaderEnabled) {
        globalStore.endRequest(req.url);
      }
    }),
  );
}

export const addToken = (
  req: HttpRequest<unknown>,
  token: string,
  disableOrgSwitching: boolean,
) => {
  return req.clone({
    withCredentials:
      !disableOrgSwitching && req.url.startsWith(environment.baseUrl)
        ? true
        : undefined,
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
};
